import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Doughnut, Line } from 'react-chartjs-2';
import { Link, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../config/config';
import '../css/Inquiry.css';
import NavigationBar from '../components/NavigationBar';
import { Chart } from 'chart.js';



export default function Inquiry() {
    const [inquiries, setInquiries] = useState([]);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [category, setCategory] = useState('');
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();
    const inquiryTitleRef = useRef(null);
    const inquiryContentRef = useRef(null); 
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 5; // 페이지당 항목 수
    const [filterType, setFilterType] = useState('all'); // 추가

    // 필터 변경 이벤트 감지
    useEffect(() => {
        const handleFilterChange = (event) => {
            setFilterType(event.detail.type);
        };
        window.addEventListener('filterChange', handleFilterChange);
        return () => window.removeEventListener('filterChange', handleFilterChange);
    }, []);

    useEffect(() => {
        // 토큰 검증
        const token = localStorage.getItem('token');
        if (!token) {
            alert('로그인이 필요합니다.');
            navigate('/');
            return;
        }

        try {
            const decodedToken = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            
            if (decodedToken.exp < currentTime) {
                alert('세션이 만료되었습니다. 다시 로그인해주세요.');
                localStorage.removeItem('token');
                navigate('/');
                return;
            }
        } catch (error) {
            console.error('토큰 검증 실패:', error);
            alert('인증에 실패했습니다. 다시 로그인해주세요.');
            localStorage.removeItem('token');
            navigate('/');
            return;
        }

        // 문의 목록 가져오기
        fetchInquiries();
    }, [navigate, page, filterType]); // filterType 의존성 추가

    const fetchInquiries = async () => {
        try {
            const token = localStorage.getItem('token');
            const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';

            // API 요청 파라미터 설정
            let params = {};
            
            if (isSuperAdmin) {
                // 슈퍼 관리자는 is_super_admin 파라미터만 전송
                params = { is_super_admin: 'true' };
            } else {
                // 일반 관리자의 경우에만 관리자 정보 검증 및 필터 적용
                const hospitalId = localStorage.getItem('hospital_id');
                const regionCode = localStorage.getItem('region_code');
                const districtCode = localStorage.getItem('district_code');

                if (!hospitalId || !regionCode || !districtCode) {
                    console.error('관리자 정보가 없습니다:', { hospitalId, regionCode, districtCode });
                    alert('관리자 정보가 올바르지 않습니다. 다시 로그인해주세요.');
                    navigate('/');
                    return;
                }

                if (filterType === 'all') {
                    params = {
                        hospital_id: hospitalId,
                        region_code: regionCode,
                        district_code: districtCode
                    };
                } else if (filterType === 'region') {
                    params = {
                        region_code: regionCode,
                        district_code: districtCode
                    };
                } else if (filterType === 'hospital') {
                    params = {
                        hospital_id: hospitalId,
                        region_code: regionCode,
                        district_code: districtCode
                    };
                }
            }

            console.log('API 요청 파라미터:', params);

            const response = await axios.get(`${API_BASE_URL}/api/getInquiries`, {
                headers: { 'Authorization': `Bearer ${token}` },
                params: params
            });

            if (response.data.success) {
                const inquiryData = response.data.data.map(inquiry => ({
                    id: inquiry.id,
                    category: inquiry.category,
                    status: inquiry.status,
                    title: inquiry.title,
                    content: inquiry.content,
                    answer: inquiry.answer,
                    created_id: inquiry.created_id,
                    created_at: inquiry.created_at,
                    answered_id: inquiry.answered_id,
                    answered_at: inquiry.answered_at
                }));

                // 페이지네이션 계산
                const totalItems = inquiryData.length;
                const calculatedTotalPages = Math.ceil(totalItems / itemsPerPage);
                setTotalPages(calculatedTotalPages);

                // 현재 페이지의 데이터만 슬라이스
                const startIndex = (page - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const paginatedData = inquiryData.slice(startIndex, endIndex);
                
                setInquiries(paginatedData);
            } else {
                throw new Error(response.data.message || '문의 목록 조회에 실패했습니다.');
            }
        } catch (error) {
            console.error('문의 목록 조회 실패:', error);
            alert(error.response?.data?.message || '문의 목록을 가져오는데 실패했습니다.');
            setInquiries([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');
            const hospitalId = localStorage.getItem('hospital_id');
            const regionCode = localStorage.getItem('region_code');
            const districtCode = localStorage.getItem('district_code');

            if (!hospitalId || !regionCode || !districtCode) {
                console.error('관리자 정보가 없습니다:', { hospitalId, regionCode, districtCode });
                alert('관리자 정보가 올바르지 않습니다. 다시 로그인해주세요.');
                navigate('/');
                return;
            }

            const response = await axios.post(`${API_BASE_URL}/api/inquiries`, {
                title,
                content,
                category,
                created_id: userId,
                status: '답변대기중',
                created_at: new Date(),
                hospital_id: hospitalId,
                region_code: regionCode,
                district_code: districtCode
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.success) {
                alert('문의가 등록되었습니다.');
                setTitle('');
                setContent('');
                setCategory('');
                setIsModalOpen(false);
                fetchInquiries();
            } else {
                throw new Error(response.data.message || '문의 등록에 실패했습니다.');
            }
        } catch (error) {
            console.error('문의 등록 실패:', error);
            alert(error.response?.data?.message || '문의 등록에 실패했습니다.');
        }
    };

    const handleInputChange = (field) => (e) => {
        e.preventDefault(); // 이벤트 전파 방지
        const value = e.target.value;
        switch(field) {
            case 'title':
                setTitle(value);
                break;
            case 'content':
                setContent(value);
                break;
            case 'category':
                setCategory(value);
                break;
            default:
                break;
        }
    };

    const InquiryModal = () => {
        const titleRef = useRef(null);
        const contentRef = useRef(null);
        
        // 각 필드를 위한 로컬 state 추가
        const [modalTitle, setModalTitle] = useState('');
        const [modalContent, setModalContent] = useState('');
        const [modalCategory, setModalCategory] = useState('');

        // 모달이 열릴 때 초기화 및 포커스
        useEffect(() => {
            setModalTitle('');
            setModalContent('');
            setModalCategory('');
            if (titleRef.current) {
                titleRef.current.focus();
            }
        }, []);

        const handleModalSubmit = async (e) => {
            e.preventDefault();
            try {
                const token = localStorage.getItem('token');
                const userId = localStorage.getItem('id');
                const hospitalId = localStorage.getItem('hospital_id');
                const regionCode = localStorage.getItem('region_code');
                const districtCode = localStorage.getItem('district_code');

                if (!hospitalId || !regionCode || !districtCode) {
                    console.error('관리자 정보가 없습니다:', { hospitalId, regionCode, districtCode });
                    alert('관리자 정보가 올바르지 않습니다. 다시 로그인해주세요.');
                    navigate('/');
                    return;
                }

                const response = await axios.post(`${API_BASE_URL}/api/inquiries`, {
                    title: modalTitle,
                    content: modalContent,
                    category: modalCategory,
                    created_id: userId,
                    status: '답변대기중',
                    created_at: new Date(),
                    hospital_id: hospitalId,
                    region_code: regionCode,
                    district_code: districtCode
                }, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.data.success) {
                    alert('문의가 등록되었습니다.');
                    setModalTitle('');
                    setModalContent('');
                    setModalCategory('');
                    setIsModalOpen(false);
                    fetchInquiries();
                } else {
                    throw new Error(response.data.message || '문의 등록에 실패했습니다.');
                }
            } catch (error) {
                console.error('문의 등록 실패:', error);
                alert(error.response?.data?.message || '문의 등록에 실패했습니다.');
            }
        };

        const handleKeyDown = (e) => {
            e.stopPropagation();
        };

        return (
            <div className="inquiry-modal-overlay" onClick={() => setIsModalOpen(false)}>
                <div className="inquiry-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="inquiry-modal-header">
                        <h2>문의하기</h2>
                        <button 
                            className="inquiry-modal-close-btn"
                            onClick={() => setIsModalOpen(false)}
                        >
                    
                        </button>
                    </div>
                    <form onSubmit={handleModalSubmit} className="inquiry-modal-body">
                        <div className="inquiry-form-group">
                            <label>제목 :</label>
                            <input
                                ref={titleRef}
                                type="text"
                                placeholder="제목을 입력해주세요"
                                value={modalTitle}
                                onChange={(e) => setModalTitle(e.target.value)}
                                onKeyDown={handleKeyDown}
                                required
                            />
                        </div>
                        <div className="inquiry-form-group">
                            <label>카테고리 :</label>
                            <select
                                value={modalCategory}
                                onChange={(e) => setModalCategory(e.target.value)}
                                required
                            >
                                <option value="">카테고리 선택</option>
                                <option value="일반">일반 문의</option>
                                <option value="기술">기술 문의</option>
                                <option value="서비스">서비스 문의</option>
                                <option value="기타">기타</option>
                            </select>
                        </div>
                        <div className="inquiry-form-group">
                            <label>내용 :</label>
                            <textarea
                                ref={contentRef}
                                className="inquiry-content-textarea"
                                placeholder="내용을 입력하세요"
                                value={modalContent}
                                onChange={(e) => setModalContent(e.target.value)}
                                onKeyDown={handleKeyDown}
                                required
                            />
                        </div>
                        <div className="inquiry-modal-footer">
                            <button type="submit" className="inquiry-submit-btn">등록</button>
                            <button 
                                type="button" 
                                className="inquiry-cancel-btn"
                                onClick={() => setIsModalOpen(false)}
                            >
                                취소
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    const InquiryDetailModal = ({ selectedInquiry, onClose }) => {
        const answerRef = useRef(null);
        const [answerText, setAnswerText] = useState('');

        useEffect(() => {
            setAnswerText(selectedInquiry.answer || '');
            if (!selectedInquiry.answer && answerRef.current) {
                answerRef.current.focus();
            }
        }, [selectedInquiry]);

        const handleAnswerSubmit = async () => {
            try {
                const token = localStorage.getItem('token');
                const userId = localStorage.getItem('id');
                const hospitalId = localStorage.getItem('hospital_id');
                const regionCode = localStorage.getItem('region_code');
                const districtCode = localStorage.getItem('district_code');

                if (!hospitalId || !regionCode || !districtCode) {
                    console.error('관리자 정보가 없습니다:', { hospitalId, regionCode, districtCode });
                    alert('관리자 정보가 올바르지 않습니다. 다시 로그인해주세요.');
                    navigate('/');
                    return;
                }

                if (!answerText || !userId) {
                    alert('답변 내용을 입력해주세요.');
                    return;
                }

                const response = await axios.put(`${API_BASE_URL}/api/inquiries/${selectedInquiry.id}/answer`, {
                    answer: answerText,
                    answered_id: userId,
                    answered_at: new Date().toISOString(),
                    status: '답변완료',
                    hospital_id: hospitalId,
                    region_code: regionCode,
                    district_code: districtCode
                }, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.data.success) {
                    alert(response.data.message);
                    fetchInquiries();
                    onClose();
                }
            } catch (error) {
                console.error('답변 등록 실패:', error);
                if (error.response?.status === 404) {
                    alert('해당 문의를 찾을 수 없습니다.');
                } else {
                    alert(error.response?.data?.message || '답변 등록 중 오류가 발생했습니다.');
                }
            }
        };

        return (
            <div className="inquiry-detail-modal-overlay" onClick={onClose}>
                <div className="inquiry-detail-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="inquiry-detail-modal-header">
                        <h2>문의 상세</h2>
                        <button 
                            className="inquiry-detail-modal-close-btn"
                            onClick={onClose}
                        >
                            ✕
                        </button>
                    </div>
                    <div className="inquiry-detail-modal-body">
                        <div className="inquiry-detail-item">
                            <div style={{ display: 'flex', gap: '20px' }}>
                                <div>
                                    <h3>제목</h3>
                                    <p>{selectedInquiry.title}</p>
                                </div>
                                <div>
                                    <h3>카테고리</h3>
                                    <p>{selectedInquiry.category}</p>
                                </div>
                                <div>
                                    <h3>답변상태</h3>
                                    <p>{selectedInquiry.status}</p>
                                </div>
                            </div>
                        </div>
                        <div className="inquiry-detail-item">
                            <h3>문의내용</h3>
                            <p>{selectedInquiry.content}</p>
                        </div>
                        {selectedInquiry.answer ? (
                            <div className="inquiry-detail-item">
                                <h3>답변내용</h3>
                                <p>{selectedInquiry.answer}</p>
                            </div>
                        ) : (
                            <div className="inquiry-detail-item">
                                <h3>답변작성</h3>
                                <textarea
                                    ref={answerRef}
                                    className="inquiry-form-textarea"
                                    placeholder="답변을 입력해주세요"
                                    value={answerText}
                                    onChange={(e) => setAnswerText(e.target.value)}
                                />
                                <div className="inquiry-modal-footer">
                                    <button 
                                        className="inquiry-submit-btn"
                                        onClick={handleAnswerSubmit}
                                    >
                                        답변등록
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="inquiry-container">
            <NavigationBar />
            <div className="inquiry-content">
                <div className="inquiry-header-container">
                    <h1>1:1 문의</h1>
                    <button 
                        className="create-inquiry-btn"
                        onClick={() => setIsModalOpen(true)}
                    >
                        문의하기
                    </button>
                </div>

                {/* 문의 목록 */}
                <div className="inquiry-list">
                    <h2>문의 목록</h2>
                    {inquiries.length === 0 ? (
                        <div className="no-inquiries">
                            <p>등록된 문의가 없습니다.</p>
                        </div>
                    ) : (
                        <>
                            <table className="inquiry-table">
                                <thead>
                                    <tr>
                                        <th>번호</th>
                                        <th>카테고리</th>
                                        <th>제목</th>
                                        <th>상태</th>
                                        <th>작성일</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {inquiries.map((inquiry) => (
                                        <tr 
                                            key={inquiry.id}
                                            className="inquiry-table-row"
                                            onClick={() => setSelectedInquiry(inquiry)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td>{inquiry.id}</td>
                                            <td>{inquiry.category}</td>
                                            <td>{inquiry.title}</td>
                                            <td>{inquiry.status}</td>
                                            <td>{new Date(inquiry.created_at).toLocaleDateString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="inquiry-pagination">
                                <button
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                    className={"inquiry-pagination-prev"}
                                >
                                    {'<'}
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <button
                                        key={i + 1}
                                        className={page === i + 1 ? 'active' : ''}
                                        onClick={() => setPage(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={page === totalPages}
                                    className={"inquiry-pagination-next"}
                                >
                                    {'>'}
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* 문의 상세 모달 */}
                {selectedInquiry && (
                    <InquiryDetailModal 
                        selectedInquiry={selectedInquiry} 
                        onClose={() => setSelectedInquiry(null)}
                    />
                )}

                {/* 문의 작성 Modal */}
                {isModalOpen && <InquiryModal />}
            </div>
        </div>
    );
}